import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { Eye, EyeOff } from 'lucide-react';

/**
 * LoginForm Props
 * 
 * Reusable login form component extracted from AuthDialog.
 * Contains all login UI logic and validation.
 */
export interface LoginFormProps {
    /** Callback for email/password login */
    onLogin: (email: string, password: string) => Promise<void>;

    /** Callback for Google authentication */
    onGoogleAuth?: () => Promise<void>;

    /** Whether the form is submitting */
    isLoading?: boolean;

    /** Whether this is the web platform (affects sizing) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;

    /** Show forgot password link */
    showForgotPassword?: boolean;

    /** Callback for forgot password */
    onForgotPassword?: () => void;

    isGoogleButtonTop?: boolean;
}

interface FormData {
    email: string;
    password: string;
}

interface FormErrors {
    email?: string;
    password?: string;
    general?: string;
}

/**
 * LoginForm
 * 
 * Reusable login form component with email/password validation.
 * Extracted from AuthDialog to enable reuse across different contexts:
 * - AuthDialog (dialog mode)
 * - LoginTemplate (full page mode)
 * - Any other component that needs login functionality
 * 
 * Features:
 * - Email validation
 * - Password visibility toggle
 * - Form validation
 * - Error handling
 * - Loading states
 * - Optional forgot password link
 * 
 * Usage:
 * ```tsx
 * <LoginForm
 *   onLogin={handleLogin}
 *   isLoading={isLoading}
 *   isWeb={true}
 *   showForgotPassword={true}
 *   onForgotPassword={() => navigate('/forgot-password')}
 * />
 * ```
 */
export const LoginForm: React.FC<LoginFormProps> = ({
    onLogin,
    onGoogleAuth,
    isLoading = false,
    isWeb = true,
    initialError,
    showForgotPassword = false,
    onForgotPassword,
    isGoogleButtonTop = false,
}) => {
    const { t } = useTranslation();
    // Form state
    const [formData, setFormData] = useState<FormData>({
        email: '',
        password: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);

    // Set initial error if provided
    useEffect(() => {
        if (initialError) {
            setErrors({ general: initialError });
        }
    }, [initialError]);

    // Form validation
    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = t('auth.fieldRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('auth.invalidEmail');
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = t('auth.fieldRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    // Handle input changes
    const handleInputChange = useCallback(
        (field: keyof FormData, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            // Clear specific field error
            if (errors[field]) {
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            }
        },
        [errors]
    );

    // Handle form submission
    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            if (!validateForm() || isLoading) return;

            try {
                setErrors({}); // Clear any previous errors
                await onLogin(formData.email, formData.password);
            } catch (error: any) {
                // Handle API errors
                setErrors({
                    general: error?.message || t('auth.loginFailed'),
                });
            }
        },
        [formData, validateForm, isLoading, onLogin]
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Google Sign-in button */}

            {onGoogleAuth && (
                <>
                    <Button
                        type="button"
                        variant="secondary-gray"
                        size={isWeb ? 'xl' : 'xs'}
                        onClick={onGoogleAuth}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 font-semibold"
                        data-track="google_oauth"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                            <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853" />
                            <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                            <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
                        </svg>
                        {t('auth.signInGoogle')}
                    </Button>
                    <div className="relative flex justify-center text-sm"><span className="text-gray-700 dark:text-gray-400">{t('auth.orLoginEmail')}</span></div>

                </>
            )}

            {/* Email field */}
            <div>
                <Label htmlFor="email" className="block font-medium text-sm mb-[6px] text-gray-700 dark:text-gray-300">
                    {t('auth.email')}
                </Label>
                <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={t('auth.enterEmail')}
                    className={`${errors.email ? 'border-red-500' : ''} placeholder:text-gray-400 border-gray-300 dark:border-gray-600`}
                    disabled={isLoading}
                />
                {errors.email && (
                    <p className="text-sm text-red-500 mt-1" data-testid="error-email">
                        {errors.email}
                    </p>
                )}
            </div>

            {/* Password field */}
            <div>
                <Label htmlFor="password" className="block font-medium text-sm text-gray-700 mb-[6px] dark:text-gray-300">
                    {t('auth.password')}
                </Label>
                <div className="relative">
                    <Input
                        id="password"
                        data-testid="input-password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder={t('auth.enterPassword')}
                        className={`${errors.password ? 'border-red-500' : ''} placeholder:text-gray-400 border-gray-300 dark:border-gray-600`}
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        disabled={isLoading}
                    >
                        {showPassword ? (
                            <EyeOff size={16} strokeWidth={2} />
                        ) : (
                            <Eye size={16} strokeWidth={2} />
                        )}
                    </button>
                </div>

                {errors.password && (
                    <p className="text-sm text-red-500 mt-1" data-testid="error-password">
                        {errors.password}
                    </p>
                )}

                {showForgotPassword && onForgotPassword && (
                    <button
                        type="button"
                        onClick={onForgotPassword}
                        className="text-sm text-primary-600 hover:text-primary-700 font-semibold mt-3 dark:text-primary-400 dark:hover:text-primary-300"
                        disabled={isLoading}
                    >
                        {t('auth.forgotPassword')}
                    </button>
                )}
            </div>

            {/* General error message */}
            {errors.general && (
                <div
                    className="p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-900/40"
                    data-testid="error-general"
                >
                    <p className="text-sm text-red-600 dark:text-red-400">{t('auth.wrongCredentials')}</p>
                </div>
            )}

            {/* Submit button */}
            <Button
                id="btn-submit-login"
                data-testid="btn-submit-auth"
                data-track="login_button"
                type="submit"
                variant="primary"
                size={isWeb ? 'lg' : 'xs'}
                disabled={isLoading}
                className="w-full font-semibold mt-2"
            >
                {isLoading ? (
                    <div className="flex items-center gap-2 justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('auth.loggingIn')}
                    </div>
                ) : (
                    t('auth.login')
                )}
            </Button>
        </form>
    );
};
